import { ProductBacklogItemEntity } from '../../../domain/entity/ProductBacklogEntity'
import { ProjectPlanCumulation } from '../../../lib/functions/projectPlan'
import { SprintDetail } from '../../../lib/functions/sprint'
import { UserBasic } from '../../../lib/functions/user'
import {
  createRowByResponse as createWbsItemRowByResponse,
  WbsItemRow,
} from '../../../lib/functions/wbsItem'
import { generateUuid } from '../../../utils/uuids'
import { TreeRow } from '../../containers/BulkSheetView/model'
import {
  getWorkloadUnitValue,
  round,
  WorkloadUnit,
} from '../../../lib/functions/workload'
import { CommentSummary } from '../../../store/comments'
import { AttachmentSummary } from '../../../utils/attachment'
import { WbsItemTypeVO } from '../../../domain/value-object/WbsItemTypeVO'

export interface SprintProductItemDetail {
  uuid: string
  sprintUuid: string
  estimatedHour: number
  taskEstimatedHour: number
  displayOrder: number
  lockVersion: number
  // Sprint×PBI単位のPBI担当者
  assignee?: UserBasic | null
  // Sprint×PBI単位のスプリントゴール
  sprintGoal?: string | null
}

// [Row models]
// SprintRow is a parent row.
export interface SprintRow extends TreeRow {
  name: string
  rowType: 'sprint'
  isUnset: boolean
  sprintDetail?: SprintDetail
}
// ProductBacklogItemRow is a child row.
export interface ProductBacklogItemRow extends TreeRow {
  originalUuid: string
  wbsItem: WbsItemRow
  cumulation?: ProjectPlanCumulation
  rowType: 'productBacklogItem'

  // 以下はこの行の親スプリントに対応するSprintProductItemの値
  // PBI担当者 (Sprint×PBI単位)
  assignee?: UserBasic | null

  // スプリントゴール (Sprint×PBI単位)
  sprintGoal?: string | null

  projectPlanPath: string
  parentWbsItem?: {
    uuid: string
    projectUuid: string
    displayName: string
    wbsItemType: WbsItemTypeVO
  }
  commentSummary?: CommentSummary
  deliverableAttachmentSummary?: AttachmentSummary
  sprintProductItems?: SprintProductItemDetail[]
}

// [SprintRow factory methods]
export const createUnsetSprintRow = (): SprintRow => {
  const rowUuid = generateUuid()
  return {
    uuid: rowUuid,
    name: 'スプリント未設定',
    treeValue: [rowUuid],
    rowType: 'sprint',
    isUnset: true,
  }
}
export const UNSET_SPRINT_ROW: SprintRow = createUnsetSprintRow()
export const createSprintRowByResponse = (sprint: SprintDetail): SprintRow => {
  return {
    name: sprint.name,
    uuid: sprint.uuid,
    treeValue: [sprint.uuid],
    rowType: 'sprint',
    isUnset: false,
    sprintDetail: sprint,
  }
}

// [ProductBacklogItemRow factory methods]
export const createProductBacklogItemRowByResponse = (
  sprintUuid: string,
  productBacklogItem: ProductBacklogItemEntity
): ProductBacklogItemRow => {
  const rowUuid = productBacklogItem.uuid
  const parentWbsItem = productBacklogItem.parentWbsItem
  // 該当スプリントに対応するSprintProductItemを取得
  const currentSprintProductItem = productBacklogItem.sprintProductItems?.find(
    spi => spi.sprintUuid === sprintUuid
  )
  const wbsItem = createWbsItemRowByResponse(productBacklogItem.deliverable)
  return {
    rowType: 'productBacklogItem',
    uuid: rowUuid,
    originalUuid: productBacklogItem.uuid,
    treeValue: [sprintUuid, rowUuid],
    wbsItem,

    // PBI担当者 (Sprint×PBI単位) - 該当スプリントのSprintProductItemから取得
    // SprintProductItemがない場合は成果物担当者をデフォルト値として使用
    assignee:
      currentSprintProductItem?.assignee ??
      (wbsItem.assignee as UserBasic | undefined),

    // スプリントゴール (Sprint×PBI単位) - 該当スプリントのSprintProductItemから取得
    sprintGoal: currentSprintProductItem?.sprintGoal,

    projectPlanPath: productBacklogItem.projectPlanPath,
    parentWbsItem: parentWbsItem
      ? {
          uuid: parentWbsItem.uuid,
          projectUuid: parentWbsItem.projectUuid,
          displayName: parentWbsItem.displayName,
          wbsItemType: new WbsItemTypeVO(parentWbsItem.typeDto),
        }
      : undefined,
    cumulation: productBacklogItem.cumulation,
    commentSummary: productBacklogItem.commentSummary,
    deliverableAttachmentSummary:
      productBacklogItem.deliverableAttachmentSummary,
    sprintProductItems:
      productBacklogItem.sprintProductItems?.map(spi => ({
        uuid: spi.uuid,
        sprintUuid: spi.sprintUuid,
        estimatedHour: spi.estimatedHour,
        taskEstimatedHour: spi.taskEstimatedHour,
        displayOrder: spi.displayOrder ?? 0,
        lockVersion: spi.lockVersion,
        assignee: spi.assignee,
        sprintGoal: spi.sprintGoal,
      })) || [],
    editedData: {}, // Initialize to share edited data with the same pbi rows associated with multiple sprints.
  }
}
export const copyProductBacklogItemRowForAssociatedWithMultiSprints = (
  sprintUuid: string,
  row: ProductBacklogItemRow
): ProductBacklogItemRow => {
  const rowUuid = generateUuid()
  // 新しいスプリントに対応するSprintProductItemから assignee と sprintGoal を取得
  const currentSprintProductItem = row.sprintProductItems?.find(
    spi => spi.sprintUuid === sprintUuid
  )
  return {
    // Use the same wbsItem object because it is shared across sprints.
    ...row,
    uuid: rowUuid,
    treeValue: [sprintUuid, rowUuid],
    // 新しいスプリントに対応する assignee と sprintGoal を設定
    // SprintProductItemがない場合は成果物担当者をデフォルト値として使用
    assignee:
      currentSprintProductItem?.assignee ??
      (row.wbsItem.assignee as UserBasic | undefined),
    sprintGoal: currentSprintProductItem?.sprintGoal,
  }
}

export type ProductBacklogRow = SprintRow | ProductBacklogItemRow

// [Row methods]
export const isSprintRow = (row: ProductBacklogRow) => {
  return row.rowType === 'sprint'
}
export const isUnsetSprintRow = (row: ProductBacklogRow) => {
  return isSprintRow(row) && row.isUnset
}
export const isUnsetSprintRowUuid = (uuid: string) => {
  return UNSET_SPRINT_ROW.uuid === uuid
}
export const isProductBacklogItemRow = (row: ProductBacklogRow) => {
  return row.rowType === 'productBacklogItem'
}
export const isAssociatedWithMultiSprints = (
  row: ProductBacklogRow
): boolean => {
  return Boolean(
    isProductBacklogItemRow(row) &&
      row.wbsItem.sprints &&
      row.wbsItem.sprints.length > 1
  )
}
export const isAssociatedWithTheSprint = (
  row: ProductBacklogRow,
  sprintUuid: string
): boolean => {
  return Boolean(
    isProductBacklogItemRow(row) &&
      row.wbsItem.sprints &&
      row.wbsItem.sprints.some(s => s.uuid === sprintUuid)
  )
}
export const isAssociatedWithSomeSprint = (row: ProductBacklogRow): boolean => {
  return Boolean(
    isProductBacklogItemRow(row) &&
      row.wbsItem.sprints &&
      row.wbsItem.sprints.length > 0
  )
}
export const isNotAssociatedWithAnySprint = (
  row: ProductBacklogRow
): boolean => {
  return Boolean(
    isProductBacklogItemRow(row) &&
      (!row.wbsItem.sprints || row.wbsItem.sprints.length === 0)
  )
}
export const getWorkload = (
  pbiRow: ProductBacklogItemRow,
  workloadUnit: WorkloadUnit
): number => {
  return getWorkloadUnitValue(pbiRow.wbsItem.estimatedWorkload, workloadUnit)
}
export const getWorkloadDistributedToMultiSprints = (
  pbiRow: ProductBacklogItemRow,
  workloadUnit: WorkloadUnit
): number => {
  const workload = getWorkloadUnitValue(
    pbiRow.wbsItem.estimatedWorkload,
    workloadUnit
  )
  const sprintCount =
    pbiRow.wbsItem.sprints && pbiRow.wbsItem.sprints.length > 0
      ? pbiRow.wbsItem.sprints.length
      : 1

  return round(workload / sprintCount)
}
export const setSprintOfRow = (
  row: ProductBacklogRow,
  sprint: SprintDetail | undefined
) => {
  if (!isProductBacklogItemRow(row)) return
  const pbiRow = row
  // editedDataに変更前の値を記録
  if (!pbiRow.editedData) {
    pbiRow.editedData = {}
  }
  if (!pbiRow.editedData.hasOwnProperty('wbsItem.sprint')) {
    pbiRow.editedData['wbsItem.sprint'] = row.wbsItem.sprint
    pbiRow.editedData['wbsItem.sprints'] = row.wbsItem.sprints
  }

  pbiRow.edited = true
  pbiRow.wbsItem.sprint = sprint ?? undefined
  pbiRow.wbsItem.sprints = sprint ? [sprint] : []
  pbiRow.treeValue = [sprint?.uuid || UNSET_SPRINT_ROW.uuid, pbiRow.uuid]

  // スプリント未割り当てへの移動時はスプリントゴールとPBI担当者をクリア
  if (!sprint) {
    pbiRow.sprintGoal = undefined
    pbiRow.assignee = undefined
  }
}
export const removeSprintOfRow = (
  row: ProductBacklogRow,
  sprint: SprintDetail
) => {
  if (!isProductBacklogItemRow(row)) return
  const pbiRow = row
  // editedDataに変更前の値を記録
  if (!pbiRow.editedData) {
    pbiRow.editedData = {}
  }
  const beforeSprint = row.wbsItem.sprint
  const beforeSprints = row.wbsItem.sprints
  if (!beforeSprints || beforeSprints.length === 0) return

  const afterSprints = beforeSprints.filter(s => s.uuid !== sprint.uuid)
  // Do nothing if the sprint is not associated.
  if (beforeSprints.length === afterSprints.length) return

  if (!pbiRow.editedData.hasOwnProperty('wbsItem.sprint')) {
    pbiRow.editedData['wbsItem.sprint'] = beforeSprint
    pbiRow.editedData['wbsItem.sprints'] = beforeSprints
  }

  pbiRow.edited = true
  pbiRow.wbsItem.sprint = afterSprints.length > 0 ? afterSprints[0] : undefined
  pbiRow.wbsItem.sprints = afterSprints
  pbiRow.treeValue = [
    afterSprints.length === 0 ? UNSET_SPRINT_ROW.uuid : '',
    pbiRow.uuid,
  ]
}
export const addSprintToRow = (
  row: ProductBacklogRow,
  sprint: SprintDetail
) => {
  if (!isProductBacklogItemRow(row)) return
  const pbiRow = row
  // editedDataに変更前の値を記録
  if (!pbiRow.editedData) {
    pbiRow.editedData = {}
  }
  if (!pbiRow.editedData.hasOwnProperty('wbsItem.sprint')) {
    pbiRow.editedData['wbsItem.sprint'] = pbiRow.wbsItem.sprint
    pbiRow.editedData['wbsItem.sprints'] = pbiRow.wbsItem.sprints
  }

  const beforeSprints = pbiRow.wbsItem.sprints
  const afterSprints = beforeSprints ? [...beforeSprints, sprint] : [sprint]
  afterSprints.sort((a, b) => a.num - b.num)
  pbiRow.edited = true
  pbiRow.wbsItem.sprint = afterSprints.length > 0 ? afterSprints[0] : undefined
  pbiRow.wbsItem.sprints = afterSprints
}

export const createSprintProductItemKey = (sprintUuid: string): string => {
  return `sprintProductItem.${sprintUuid}.estimatedHour`
}

export const createSprintProductItemAssigneeKey = (
  sprintUuid: string
): string => {
  return `sprintProductItem.${sprintUuid}.assignee`
}

export const createSprintProductItemSprintGoalKey = (
  sprintUuid: string
): string => {
  return `sprintProductItem.${sprintUuid}.sprintGoal`
}
